/**
 * useApmRouteTracker
 *
 * Creates an OpenTelemetry span for each client-side route navigation.
 * This is necessary because react-router-dom v7's `createBrowserRouter` /
 * `RouterProvider` API is incompatible with the `ApmRoutes` helper, so we
 * track route changes manually via `useLocation`.
 *
 * Must be called inside a component that is rendered within a Router context
 * (i.e., inside RouterProvider). The App component is the right place.
 */

import { SpanStatusCode, trace } from "@opentelemetry/api";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/** Map a pathname to a normalised transaction name by replacing dynamic
 * segments (UUIDs, slugs, etc.) with their route parameter placeholders. */
function getTransactionName(pathname: string): string {
  return (
    pathname
      // /artist/some-slug-123  →  /artist/:id
      .replace(/\/artist\/[^/]+/, "/artist/:id")
      // /performance/some-slug  →  /performance/:id
      .replace(/\/performance\/[^/]+/, "/performance/:id")
      // /stage/some-slug  →  /stage/:id
      .replace(/\/stage\/[^/]+/, "/stage/:id")
  );
}

export function useApmRouteTracker(): void {
  const location = useLocation();
  const prevName = useRef<string | null>(null);

  useEffect(() => {
    const name = getTransactionName(location.pathname);

    // Skip duplicate navigations (e.g., search-param-only changes on /).
    if (name === prevName.current) return;
    prevName.current = name;

    // Create a short span to mark the route change in the trace view.
    const tracer = trace.getTracer("tilos-maraton-router");
    const span = tracer.startSpan(`route: ${name}`);
    span.setStatus({ code: SpanStatusCode.OK });
    span.end();
  }, [location.pathname]);
}
